// BUILD-046 J1a (Customer association): the searchable, optional "Customer" field of the Assign task dialog.
//
// A text input (role=combobox) sits in the left 60% of the Customer + Task due date row of Assign task (cycle C; it was directly below the Task title row before).
// Its results list spans the whole row (`wideList`), wraps long names and scrolls inside its own max height. Typing two or more characters asks the limited customer directory (after a 250 ms
// pause); a listbox shows at most 20 active customers (name first, then customer and code when they add something). One customer is chosen by a click on a row or
// Enter on the highlighted row; it then shows as a chip with a Remove button. Tab, Escape, a click outside and typing alone never choose a customer.
// The three states of a lookup are kept honest: results, "No customers match" (only after the directory answered with nothing) and "can't be checked right
// now" with Retry. A failure is never shown as "no matches", and it never discards what was typed.
// All rules (planning, stale answers, failure wording, keys, row wording) are in customerFieldModel.ts and peopleComboboxModel.ts; this file draws them.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { WorkCustomerSearch } from '../../api/workShared';
import { CUSTOMER_SEARCH_DELAY_MS, decideCustomerKey, describeCustomerChip, describeCustomerSearch, initialCustomerSearch, customerOptionId, normalizeCustomerQuery, planCustomerSearch, searchFailed, searchRequested, searchSucceeded } from './customerFieldModel';
import type { CustomerChoice, CustomerSearchState } from './customerFieldModel';
import { clampActive, closeList, initialCombo, openList, typeQuery } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import { DropdownShell } from './FloatingPanel';

export interface CustomerFieldProps {
  /** The input's id; the host may focus it by this id. */
  id: string;
  /** The chosen customer, or null. */
  customer: CustomerChoice | null;
  disabled: boolean;
  /** The directory lookup (`workApi.searchCustomers`). */
  search: (q: string) => Promise<WorkCustomerSearch>;
  onChoose: (customer: CustomerChoice) => void;
  onClear: () => void;
  /** What the person has typed and not chosen, reported on every change (the host stops a submit that would silently drop it). */
  onDraftChange?: (text: string) => void;
  /** A problem to show beside the field (a refused customer, typed text that was not chosen); null when none. */
  problem?: string | null;
  /**
   * Cycle C (Assign task): the results list is positioned against the nearest positioned ANCESTOR (the host's Customer + Task due date row) instead of this field,
   * so it spans the whole row, wide enough to read long names. Default false: the list is as wide as the field.
   */
  wideList?: boolean;
  /** Pass F1: the results list is drawn on the page body, against the field, so no clipping container can cut it off (the compact Edit task form). Default false: in place, as the Assign task dialog has it. */
  floating?: boolean;
  /**
   * "Search again" (after a visit to Pricing, so a customer added there can be found): each time the host changes this number the field asks the directory again for what is
   * typed, opens the list and takes focus. Absent or unchanged does nothing. A chosen customer is never disturbed.
   */
  searchAgainToken?: number;
}

export function CustomerField({ id, customer, disabled, search, onChoose, onClear, onDraftChange, problem = null, wideList = false, floating = false, searchAgainToken }: CustomerFieldProps) {
  const [combo, setCombo] = useState<ComboState>(initialCombo());
  const [state, setState] = useState<CustomerSearchState>(initialCustomerSearch());
  const latest = useRef<CustomerSearchState>(state);
  // The text in the box, for the "Search again" effect (which runs when the host moves the token, not when the text changes).
  const latestQuery = useRef(combo.query);
  latestQuery.current = combo.query;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const removeRef = useRef<HTMLButtonElement | null>(null);
  const focusAfter = useRef<'input' | 'chip' | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;
  const onDraftRef = useRef(onDraftChange);
  onDraftRef.current = onDraftChange;
  const listboxId = `${id}-listbox`;
  const hintId = `${id}-hint`;
  const problemId = `${id}-problem`;

  function apply(next: CustomerSearchState): void {
    latest.current = next;
    setState(next);
  }

  /** Reset (nothing or too short) or send the lookup. An answer is applied only if it is for the latest request. */
  function ask(raw: string): void {
    const { state: next, request } = searchRequested(latest.current, raw);
    apply(next);
    if (request === null) return;
    searchRef.current(request.q).then(
      (result) => apply(searchSucceeded(latest.current, request.seq, result)),
      (thrown: unknown) => apply(searchFailed(latest.current, request.seq, thrown))
    );
  }

  // What was typed drives the lookup: at once to reset (empty or too short), after a short pause to ask (a burst of typing sends one request).
  useEffect(() => {
    if (customer !== null) return undefined;
    const plan = planCustomerSearch(combo.query);
    if (plan.kind !== 'search') {
      ask(combo.query);
      return undefined;
    }
    const timer = setTimeout(() => ask(combo.query), CUSTOMER_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
    // `ask` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combo.query, customer]);

  // "Search again": the host moved the token (the person came back from Pricing, or pressed the button). Ask the directory again for what is typed, show the list.
  const seenToken = useRef(searchAgainToken);
  useEffect(() => {
    if (searchAgainToken === seenToken.current) return;
    seenToken.current = searchAgainToken;
    if (customer !== null) return;
    if (planCustomerSearch(latestQuery.current).kind === 'search') ask(latestQuery.current);
    setCombo((c) => openList(c));
    inputRef.current?.focus();
    // `ask` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAgainToken]);

  // The host learns what is typed and not chosen; a chosen customer leaves nothing pending.
  useEffect(() => {
    onDraftRef.current?.(customer === null ? normalizeCustomerQuery(combo.query) : '');
  }, [combo.query, customer]);

  // The rows shown now, once they are rows for the text in the box (a different earlier text shows as searching until the pause ends).
  const typed = normalizeCustomerQuery(combo.query);
  const forThisText = state.q === typed;
  const wanted = planCustomerSearch(combo.query).kind === 'search';
  const view = describeCustomerSearch(forThisText ? state : { ...state, status: wanted ? 'loading' : 'idle', customers: [], more: false, failure: null });
  useEffect(() => {
    // Rows arrived or went: keep the highlighted row inside the list.
    setCombo((c) => clampActive(c, view.rows.length));
  }, [view.rows.length]);

  // Focus follows the swap between the input and the chip, so a keyboard user is never dropped onto the page.
  useEffect(() => {
    if (focusAfter.current === 'chip') removeRef.current?.focus();
    else if (focusAfter.current === 'input') inputRef.current?.focus();
    focusAfter.current = null;
  }, [customer]);

  function choose(customerId: string): void {
    const row = view.rows.find((r) => r.customerId === customerId);
    if (row === undefined) return;
    focusAfter.current = 'chip';
    setCombo(initialCombo());
    onChoose({ customerId: row.customerId, label: row.label });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (disabled) return;
    const decision = decideCustomerKey(event.key, combo, view.rows);
    if (decision.handled) event.preventDefault();
    if (decision.stop) event.stopPropagation();
    // Enter on text that was not chosen never submits the dialog: the person would lose the customer without noticing.
    if (event.key === 'Enter' && !decision.handled && typed !== '') event.preventDefault();
    if (decision.choose !== null) {
      choose(decision.choose);
      return;
    }
    if (decision.state !== combo) setCombo(decision.state);
  }

  const showList = combo.open && !disabled && (view.status !== 'failed');
  const activeRow = showList && combo.active >= 0 && combo.active < view.rows.length ? view.rows[combo.active] : null;
  const describedBy = [problem !== null ? problemId : undefined, showList && view.moreNote !== null ? hintId : undefined].filter(Boolean).join(' ') || undefined;
  // Browser run (cycle C): the wide list can reach below the visible part of the dialog body, so the arrow keys could highlight a customer that was hidden under the footer. The
  // highlighted customer is brought into view (the list, and the dialog body behind it, scroll only as far as needed).
  const activeCustomerId = activeRow !== null ? activeRow.customerId : null;
  useEffect(() => {
    if (activeCustomerId === null) return;
    document.getElementById(customerOptionId(id, activeCustomerId))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeCustomerId, id]);

  return (
    <div ref={wrapRef} className={wideList ? undefined : 'relative'} data-testid="assign-customer-field">
      {customer !== null ? (
        <div role="group" aria-labelledby={`${id}-label`} className={`flex items-center justify-between gap-2 rounded border bg-white px-2 py-1.5 ${problem !== null ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'opacity-60' : ''}`} data-testid="assign-customer-chip">
          <span id={`${id}-chosen`} className="min-w-0 break-words text-sm text-gray-900" title={describeCustomerChip(customer).label}>
            {describeCustomerChip(customer).label}
          </span>
          <button
            ref={removeRef}
            type="button"
            disabled={disabled}
            onClick={() => {
              focusAfter.current = 'input';
              onClear();
            }}
            aria-label={describeCustomerChip(customer).removeLabel}
            title={describeCustomerChip(customer).removeLabel}
            aria-describedby={problem !== null ? problemId : undefined}
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <div
          className={`flex items-center gap-1 rounded border bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-[#4AABDE] ${problem !== null ? 'border-red-500' : 'border-gray-300'} ${disabled ? 'opacity-60' : ''}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !disabled) {
              event.preventDefault();
              inputRef.current?.focus();
            }
          }}
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="ml-1 h-4 w-4 flex-shrink-0 text-gray-400">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-labelledby={`${id}-label`}
            aria-expanded={showList}
            aria-controls={listboxId}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeRow !== null ? customerOptionId(id, activeRow.customerId) : undefined}
            aria-describedby={describedBy}
            aria-invalid={problem !== null || undefined}
            autoComplete="off"
            disabled={disabled}
            value={combo.query}
            placeholder="Search by customer name"
            onChange={(event) => setCombo((c) => typeQuery(c, event.target.value))}
            onKeyDown={onKeyDown}
            onBlur={(event) => {
              const next = event.relatedTarget;
              if (!(next instanceof Node && wrapRef.current !== null && wrapRef.current.contains(next))) setCombo((c) => closeList(c));
            }}
            className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only">
        {customer === null && combo.open ? view.announcement : ''}
      </div>

      {customer === null && showList && (
        // The wrapper keeps the pointer from taking focus out of the input (a click on a note must not close the list).
        <DropdownShell floating={floating} anchorRef={wrapRef}>
          <ul id={listboxId} role="listbox" aria-label="Customers" className={`${wideList ? 'max-h-60' : 'max-h-48'} overflow-y-auto py-1`} data-testid={`${id}-listbox`}>
            {view.rows.map((row, index) => (
              <li
                key={row.customerId}
                id={customerOptionId(id, row.customerId)}
                role="option"
                aria-selected={index === combo.active}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(row.customerId)}
                onMouseMove={() => setCombo((c) => (c.active === index ? c : { ...c, active: index }))}
                className={`cursor-pointer select-none px-3 py-1.5 text-sm text-gray-900 ${index === combo.active ? 'bg-sky-100' : 'hover:bg-gray-50'}`}
              >
                <span className="block break-words">{row.label}</span>
                {row.detail !== null && <span className="block break-words text-xs text-gray-500">{row.detail}</span>}
              </li>
            ))}
            {view.rows.length === 0 && (
              <li role="presentation" className="px-3 py-2 text-sm text-gray-500">
                {view.status === 'loading' ? 'Searching\u2026' : view.emptyText !== null ? view.emptyText : view.hint !== null ? view.hint : 'Type a customer name.'}
              </li>
            )}
          </ul>
          {view.moreNote !== null && (
            <p id={hintId} className="border-t border-gray-100 px-3 py-1 text-xs text-gray-600" data-testid={`${id}-more`}>
              {view.moreNote}
            </p>
          )}
        </DropdownShell>
      )}

      {customer === null && view.failure !== null && (
        <p role="alert" className="mt-1 text-xs text-red-700" data-testid={`${id}-failure`}>
          {view.failure.headline} {view.failure.detail}{' '}
          {view.failure.canRetry && (
            <button type="button" onClick={() => ask(combo.query)} aria-label="Retry the customer search" className="underline" data-testid={`${id}-retry`}>
              Retry
            </button>
          )}
        </p>
      )}

      {problem !== null && (
        <p id={problemId} role="alert" className="mt-1 text-sm text-red-700" data-testid={`${id}-problem`}>
          {problem}
        </p>
      )}
    </div>
  );
}
